import { handleCommonErrors } from 'utils/api'
import { apiPaths, queryKeyWithNpsn } from 'api/kinerja/kepala-sekolah/config'
import { useKinerjaApi, useKinerjaMutationApi } from 'api/kinerja/utils/api'
import {
  getContentKinerjaUtama,
  getContentPerilakuKerja,
  getSKPPerencanaan,
  updateDemoSKP,
  updateSKPState,
} from 'app/KinerjaKSPage/utils/demo/api'
import { InvalidUserUnauthorized } from 'api/kinerja/useAPIKinerja'

import type {
  ResponseContentKinerjaKSUtama,
  ResponseKinerjaKs,
  UpsertSKPKSPayload,
} from 'types/kinerja-ks'
import type { UseQueryOptions } from 'react-query'
import type {
  UpdateStatePayload,
  RequestParamsKinerja,
  ResponseContentPerilakuKerja,
  ResponseGetOverviewRealisasi,
  KinerjaWithRole,
} from 'types/kinerja'
import type { UseDemoModeOptions } from 'app/KinerjaPage/hooks/useDemoContext'

export const SKP_QUERY_KEY = 'get-skp'

function handleError(status, path, error) {
  if (status === 401) {
    throw new InvalidUserUnauthorized(path, error)
  } else {
    handleCommonErrors(status, path, error)
  }
}

function validateStatus(status: number) {
  return status < 500 && status !== 401
}

export function useAPIKinerja(
  npsn: string,
  opts?: UseDemoModeOptions<UseQueryOptions<ResponseKinerjaKs>>
) {
  const { isDemoMode, ...otherOpts } = opts ?? {}
  return useKinerjaApi<ResponseKinerjaKs>(apiPaths.kinerja, {
    queryKey: queryKeyWithNpsn(SKP_QUERY_KEY, npsn),
    ...(isDemoMode
      ? {
          queryFn: getSKPPerencanaan,
        }
      : {}),
    axiosConfig: {
      validateStatus,
      params: {
        npsn,
      },
    },
    queryConfig: {
      useErrorBoundary: true,
      ...otherOpts,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPIContentKinerjaUtama(
  params: RequestParamsKinerja = {},
  opts?: UseDemoModeOptions<UseQueryOptions<ResponseContentKinerjaKSUtama>>
) {
  const { isDemoMode, ...otherOpts } = opts ?? {}
  const { jenjang, tipe, ...restParams } = params

  return useKinerjaApi<ResponseContentKinerjaKSUtama>(
    `${apiPaths.konten}/kinerja/${jenjang}`,
    {
      ...(isDemoMode
        ? {
            queryFn: getContentKinerjaUtama,
          }
        : {}),
      axiosConfig: {
        validateStatus,
        params: { ...restParams, tipe },
      },
      queryConfig: {
        enabled: !!params.jenjang,
        useErrorBoundary: true,
        ...otherOpts,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIContentPerilakuKerja(
  params: KinerjaWithRole<UseDemoModeOptions> = {},
  opts?: UseQueryOptions<ResponseContentPerilakuKerja>
) {
  return useKinerjaApi<ResponseContentPerilakuKerja>(
    `${apiPaths.konten}/perilaku-kerja`,
    {
      ...(params?.isDemoMode
        ? {
            queryFn: getContentPerilakuKerja,
          }
        : {}),
      axiosConfig: {
        validateStatus,
        params: {
          role: params?.role,
          tipe: params?.tipe,
        },
      },
      queryConfig: {
        useErrorBoundary: true,
        ...opts,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIUpsertSKP(opts: UseDemoModeOptions = {}) {
  return useKinerjaMutationApi<any, any, UpsertSKPKSPayload>(apiPaths.kinerja, {
    ...(opts?.isDemoMode
      ? {
          mutationFn: updateDemoSKP,
        }
      : {}),
    axiosConfig: {
      method: 'POST',
    },
  })
}

export function useAPIUpdateState(opts: UseDemoModeOptions = {}) {
  return useKinerjaMutationApi<any, any, UpdateStatePayload>(
    `${apiPaths.kinerja}/update/state`,
    {
      ...(opts?.isDemoMode
        ? {
            mutationFn: updateSKPState,
          }
        : {}),
      axiosConfig: {
        method: 'PATCH',
      },
    }
  )
}

/**
 * @deprecated This function is deprecated.
 */
export function useAPIGetOverviewRealisasi() {
  return useKinerjaApi<ResponseGetOverviewRealisasi>(
    `${apiPaths.realisasiSkp}/overview`,
    {
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
      },
      errorHandlerFn: handleError,
    }
  )
}
